import { logger } from "@/lib/logger";
/**
 * persistenceMetrics.ts — Registro ligero de métricas de la capa de persistencia.
 *
 * QUÉ HACE:
 *  - Registra en memoria los últimos N eventos del motor transaccional:
 *    commits, restauraciones desde IDB, reparaciones de integridad, errores.
 *  - Persiste un resumen en localStorage (clave __altenot_metrics__) para que
 *    sobreviva recarga y sea inspeccionable desde DevTools / reporte de soporte.
 *  - No genera dependencias externas ni afecta el rendimiento del camino crítico.
 *
 * QUÉ EXPORTA: persistenceMetrics (singleton)
 *
 * USO:
 *  import { persistenceMetrics } from "@/lib/persistence/persistenceMetrics";
 *  persistenceMetrics.recordCommit("tx_001", 3, 4);
 *  persistenceMetrics.recordRestore(["caja_ventas_historial"]);
 *  const report = persistenceMetrics.getReport();
 */

export interface MetricEvent {
  ts: string;
  type: "commit" | "restore" | "integrity_repair" | "error" | "wal_recover";
  detail: Record<string, unknown>;
}

const MAX_EVENTS = 200;
const METRICS_KEY = "__altenot_metrics__";

class PersistenceMetrics {
  private events: MetricEvent[] = [];

  constructor() {
    this.loadFromStorage();
  }

  // ── Carga / guardado ──────────────────────────────────────────────────────

  private loadFromStorage(): void {
    try {
      const raw = localStorage.getItem(METRICS_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as MetricEvent[];
        if (Array.isArray(parsed)) {
          this.events = parsed.slice(-MAX_EVENTS);
        }
      }
    } catch {
      /* corrupt / unavailable: start fresh */
    }
  }

  private persist(): void {
    try {
      localStorage.setItem(
        METRICS_KEY,
        JSON.stringify(this.events.slice(-MAX_EVENTS)),
      );
    } catch {
      /* cuota llena: no perder datos de negocio por métricas */
    }
  }

  private push(event: MetricEvent): void {
    this.events.push(event);
    if (this.events.length > MAX_EVENTS) {
      this.events = this.events.slice(-MAX_EVENTS);
    }
    this.persist();
  }

  // ── API pública ───────────────────────────────────────────────────────────

  /** Registra un commit exitoso del motor transaccional. */
  recordCommit(txId: string, durationMs: number, writeCount: number): void {
    this.push({
      ts: new Date().toISOString(),
      type: "commit",
      detail: { txId, durationMs, writeCount },
    });
  }

  /** Registra una restauración de claves desde el espejo IDB. */
  recordRestore(keys: string[]): void {
    this.push({
      ts: new Date().toISOString(),
      type: "restore",
      detail: { count: keys.length, keys },
    });
  }

  /** Registra una reparación de integridad (reconstrucción de ventas/cierres). */
  recordIntegrityRepair(count: number, details: Record<string, unknown>): void {
    this.push({
      ts: new Date().toISOString(),
      type: "integrity_repair",
      detail: { count, ...details },
    });
  }

  /** Registra una recuperación del WAL al arrancar. */
  recordWalRecover(writesReplayed: number): void {
    this.push({
      ts: new Date().toISOString(),
      type: "wal_recover",
      detail: { writesReplayed },
    });
  }

  /** Registra un error de la capa de persistencia. */
  recordError(context: string, message: string): void {
    this.push({
      ts: new Date().toISOString(),
      type: "error",
      detail: { context, message },
    });
  }

  /** Devuelve el reporte completo (últimos MAX_EVENTS eventos + resumen). */
  getReport(): {
    summary: Record<string, number>;
    lastEvents: MetricEvent[];
  } {
    const summary: Record<string, number> = {
      commits: 0,
      restores: 0,
      integrity_repairs: 0,
      wal_recovers: 0,
      errors: 0,
    };
    for (const e of this.events) {
      const key =
        e.type === "commit"
          ? "commits"
          : e.type === "restore"
            ? "restores"
            : e.type === "integrity_repair"
              ? "integrity_repairs"
              : e.type === "wal_recover"
                ? "wal_recovers"
                : "errors";
      summary[key] = (summary[key] ?? 0) + 1;
    }
    return { summary, lastEvents: this.events.slice(-50) };
  }

  /**
   * Resumen de rendimiento de los commits registrados: promedio, máximo,
   * conteo y total de escrituras. Sirve para detectar degradación del POS.
   */
  getTimingSummary(): {
    commitCount: number;
    avgCommitMs: number;
    maxCommitMs: number;
    totalWrites: number;
  } {
    let count = 0;
    let sum = 0;
    let max = 0;
    let totalWrites = 0;
    for (const e of this.events) {
      if (e.type !== "commit") continue;
      const d = Number(e.detail.durationMs) || 0;
      const w = Number(e.detail.writeCount) || 0;
      count++;
      sum += d;
      totalWrites += w;
      if (d > max) max = d;
    }
    return {
      commitCount: count,
      avgCommitMs: count > 0 ? Math.round((sum / count) * 100) / 100 : 0,
      maxCommitMs: max,
      totalWrites,
    };
  }

  /** Expone el reporte en consola (para soporte / debug en campo). */
  printReport(): void {
    const { summary, lastEvents } = this.getReport();
    logger.group("[Altenot Persistence Metrics]");
    logger.table(summary);
    logger.log("Últimos eventos:", lastEvents);
    logger.groupEnd();
  }
}

export const persistenceMetrics = new PersistenceMetrics();

// Exponer en window para inspección en campo (DevTools / Eruda).
if (typeof window !== "undefined") {
  (
    window as Window & { __altenotMetrics?: PersistenceMetrics }
  ).__altenotMetrics = persistenceMetrics;
}
